import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { InsightsState } from 'app-redux/InsightsState';
import { nameof } from 'utils/typeHelpers';

export enum PanelType {
  MyLocationsPanel = 'MY_LOCATIONS',
  DiseasesPanel = 'DISEASES',
  DiseaseActivityListPanel = 'DISEASE_SUMMARY',
  DiseaseActivityDetailsPanel = 'DISEASE_ACTIVITY_SUMMARY',
  DiseaseActivityUpdatesPanel = 'DISEASE_ACTIVITY_UPDATES',
  ParametersPanel = 'PARAMETERS',
}
export type PanelVisibility = {
  canClose: boolean;
  isOpen: boolean;
  isMinimized: boolean;
};
export type DiseaseActivityParamsPayload = {
  userLocationId?: number; // NOTE: a0674a98: value of 0 is global
  diseaseId?: number;
  speciesId?: number;
  countryId?: number;
  activityDate?: string;
  hasActivity?: boolean;
  hasParameters?: boolean;
};
export type DiseaseActivityParams = DiseaseActivityParamsPayload & {
  isActive?: boolean;
};
export type UpdatePanelsPayload = {
  open: PanelType[];
  activePanel: PanelType;
};

export type DiseaseActivityPanelsState = {
  disacParams: DiseaseActivityParams;
  byPanel: Record<PanelType, PanelVisibility>;
  activePanel: PanelType;
  isProximalDetailsExpandedDELP: boolean;
  isProximalDetailsExpandedEDP: boolean;
};

export const InitialDiseaseActivityPanelsState: DiseaseActivityPanelsState = {
  disacParams: { isActive: false },
  activePanel: PanelType.MyLocationsPanel,
  byPanel: {
    [PanelType.MyLocationsPanel]: {
      canClose: false,
      isOpen: true,
      isMinimized: false,
    },
    [PanelType.DiseasesPanel]: {
      canClose: true,
      isOpen: false,
      isMinimized: false,
    },
    [PanelType.DiseaseActivityListPanel]: {
      canClose: true,
      isOpen: false,
      isMinimized: false,
    },
    [PanelType.DiseaseActivityDetailsPanel]: {
      canClose: true,
      isOpen: false,
      isMinimized: false,
    },
    [PanelType.DiseaseActivityUpdatesPanel]: {
      canClose: true,
      isOpen: false,
      isMinimized: false,
    },
    [PanelType.ParametersPanel]: {
      canClose: true,
      isOpen: false,
      isMinimized: false,
    },
  },
  isProximalDetailsExpandedDELP: false,
  isProximalDetailsExpandedEDP: false,
};

type MinimizePanelPayload = {
  panel: PanelType;
  minimize: boolean;
};
export type ProximalDetailsExpandPayload = {
  panel: 'DELP' | 'EDP' | 'all';
  isOpen: boolean;
};

const panelsSlice = createSlice({
  name: nameof<InsightsState>('diseaseActivityPanels'),
  initialState: InitialDiseaseActivityPanelsState,
  reducers: {
    minimizePanel: {
      prepare: (panel: PanelType, minimize: boolean) => ({ payload: { panel, minimize } }),
      reducer: (state, action: PayloadAction<MinimizePanelPayload>) => {
        const payload = action.payload;
        state.byPanel[payload.panel].isMinimized = payload.minimize;
      },
    },
    updatePanels: (state, action: PayloadAction<UpdatePanelsPayload>) => {
      const openPanels = action.payload.open;
      [
        PanelType.MyLocationsPanel,
        PanelType.DiseasesPanel,
        PanelType.DiseaseActivityListPanel,
        PanelType.DiseaseActivityDetailsPanel,
        PanelType.DiseaseActivityUpdatesPanel,
        PanelType.ParametersPanel,
      ].forEach(p => {
        state.byPanel[p].isOpen = openPanels.includes(p);
        state.byPanel[p].isMinimized = openPanels.indexOf(p) < openPanels.length - 2;
      });
      state.activePanel = action.payload.activePanel;
    },
    setParameters: (state, action: PayloadAction<DiseaseActivityParamsPayload>) => {
      state.disacParams = {
        ...action.payload,
        isActive: true,
      };
    },
    clearParameters: state => {
      state.disacParams = { isActive: false };
      state.isProximalDetailsExpandedDELP = false;
      state.isProximalDetailsExpandedEDP = false;
    },
    toggleProximalExpansion: (state, action: PayloadAction<ProximalDetailsExpandPayload>) => {
      const payload = action.payload;
      if (payload.panel === 'DELP' || payload.panel === 'all')
        state.isProximalDetailsExpandedDELP = payload.isOpen;
      if (payload.panel === 'EDP' || payload.panel === 'all')
        state.isProximalDetailsExpandedEDP = payload.isOpen;
    },
  },
});

const { actions, reducer } = panelsSlice;
export const {
  minimizePanel,
  setParameters,
  clearParameters,
  updatePanels,
  toggleProximalExpansion,
} = actions;
export default reducer;
